import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getProfile, newGetNotifications } from '../../services/userService';
import { useNotification } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionsContext';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from './Breadcrumbs';

function Header({ onMenuClick, collapsed }) {
  const location = useLocation();
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const { unreadCount, updateUnreadCount } = useNotification();
  const { permissions } = usePermissions();
  const { logout } = useAuth();

  useEffect(() => {
    fetchProfile();
    fetchNotifications();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await newGetNotifications({});
      if (response?.notifications) {
        updateUnreadCount(response.notifications.filter(n => n.is_read === 0).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };


  return (
    <header className={`fixed top-0 right-0 z-30  h-16 flex items-center px-4 transition-all duration-300 ${collapsed ? 'left-0 lg:left-16' : 'left-0 lg:left-64'}`}>
      <div className="flex items-center justify-between w-full">
        <div>
          <Breadcrumbs />
        </div>

        {/* Left: mobile menu toggle */}
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <Icon icon="mdi:menu" width="22" height="22" className="text-gray-600" />
        </button>

        {/* Right: Bell + Profile */}
        <div className="flex items-center gap-3">
          {permissions.notification_management_view && (
            <Link
              to="/admin/notifications"
              className="relative p-2.5 rounded-lg bg-white border border-stroke transition-colors"
            >
              <Icon icon="solar:bell-linear" width="22" height="22" className="text-font-secondary" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-error text-surface text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 font-medium">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          )}

          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg bg-white transition-colors border border-stroke"
            >
              {profileData?.profile_pic_url ? <img
                src={profileData?.profile_pic_url}
                alt="User"
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              /> : null}
              <span className={`w-8 h-8 rounded-full bg-background border border-stroke items-center justify-center ${profileData?.profile_pic_url ? 'hidden' : 'flex'}`}>
                <Icon icon="solar:user-rounded-linear" width="18" className="text-font-secondary" />
              </span>
              <div className="hidden md:flex flex-col items-start leading-tight">
                <span className="text-sm text-font-primary font-semibold max-w-[120px] truncate">
                  {profileData?.name || 'User'}
                </span>
                <span className="text-xs text-font-secondary">Admin</span>
              </div>
              <Icon icon="mdi:chevron-down" width="16" height="16" className="text-font-secondary hidden md:block" />
            </button>

            <div className={`absolute right-0 mt-2 w-52 bg-surface rounded-xl shadow-lg border border-stroke z-50 transition-all duration-200 ${dropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
              <div className="py-2">
                {permissions.profile_management_view && (
                  <Link to="/admin/profile" onClick={() => setDropdownOpen(false)} className="flex items-center px-4 py-3 text-font-primary hover:bg-background transition-colors text-sm">
                    <Icon icon="fluent:person-24-filled" className="mr-3 text-font-secondary" width="18" />
                    Profile
                  </Link>
                )}
                {permissions.setting_management_view && (
                  <Link to="/admin/settings" onClick={() => setDropdownOpen(false)} className="flex items-center px-4 py-3 text-font-primary hover:bg-background transition-colors text-sm">
                    <Icon icon="fluent:settings-24-filled" className="mr-3 text-font-secondary" width="18" />
                    Settings
                  </Link>
                )}
                {permissions.announcement_management_view && (
                  <Link to="/admin/announcement" onClick={() => setDropdownOpen(false)} className="flex items-center px-4 py-3 text-font-primary hover:bg-background transition-colors text-sm">
                    <Icon icon="material-symbols:campaign-outline" className="mr-3 text-font-secondary" width="18" />
                    Announcement
                  </Link>
                )}
                <hr className="my-1 border-stroke" />
                <button
                  onClick={() => logout()}
                  className="w-full flex items-center px-4 py-3 text-error hover:bg-error/10 transition-colors text-sm"
                >
                  <Icon icon="fluent:sign-out-24-filled" className="mr-3" width="18" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
